import React, {FC, useEffect} from 'react'
import {Trans} from 'react-i18next'

import {Box, Grid, Hidden, Theme, Typography} from '@mui/material'
import {makeStyles} from 'tss-react/mui'

import blueSideBackgroundBottom from '@progyny/assets/images/side-banner-white-bottom.png'
import Logo from '@progyny/components/Logo'
import LanguageSelector from '@progyny/components/Navigation/LanguageSelector'
import {useNavigation} from '@progyny/hooks/useNavigation'
import {useScreenSize} from '@progyny/hooks/useWindowSize'

import {BrandColors, theme} from '@root/theme'

type StyleProps = {
  backgroundPicSrc: string
  backgroundColor: string
  logoColor: keyof BrandColors
  marginLeft?: string
}

type Props = {
  styleProps: StyleProps
  mainComponent: React.ReactNode
  hideSidePanel?: boolean
}

const useStyles = makeStyles<StyleProps>()((theme: Theme, props) => ({
  main: {
    width: 'calc(100% - 500px)',
    minHeight: '480px',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  side: {
    position: 'relative',
    width: 500,
    h3: {
      letterSpacing: '0.15rem',
    },
  },
  image: {
    backgroundColor: props.backgroundColor,
    color: props.logoColor
      ? theme.palette.other.brandColors[props.logoColor]
      : 'inherit',
    height: '100%',
  },
  topImage: {
    backgroundImage: `url(${props.backgroundPicSrc})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top center',
    opacity: 0.3,
    position: 'absolute',
    top: 0,
    left: 0,
  },
  bottomImage: {
    backgroundImage: `url(${blueSideBackgroundBottom})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'bottom center',
    position: 'absolute',
    bottom: 0,
    left: 0,
    opacity: 0.3,
  },
}))

const SidepanelContainer: FC<Props> = ({
  styleProps,
  mainComponent,
  hideSidePanel = false,
}) => {
  const {logoColor, backgroundPicSrc, backgroundColor} = styleProps
  const {classes} = useStyles({backgroundPicSrc, backgroundColor, logoColor})
  const {setNav} = useNavigation()

  const {width} = useScreenSize()
  const isDownSM = width !== undefined && width < theme.breakpoints.values.sm

  useEffect(() => {
    setNav({hide: hideSidePanel || !isDownSM, hideMenu: true, variant: 'white'})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDownSM, hideSidePanel])

  if (hideSidePanel) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            height: 63,
            flexShrink: 0,
          }}
        >
          <Logo width={100} />
          <LanguageSelector />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            px: 2,
            pt: '47px',
          }}
        >
          {mainComponent}
        </Box>
      </Box>
    )
  }

  return (
    <Grid container height={'100vh'} spacing={0}>
      <Hidden mdDown>
        <Grid item className={classes.side}>
          <Box width={'100%'} height={300} className={classes.bottomImage} />
          <Box width={'100%'} height={300} className={classes.topImage} />

          <Box
            className={classes.image}
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              maxWidth={400}
              textAlign={'center'}
            >
              <Logo mb={4} width={180} fill={logoColor} variant='white' />
              <Typography
                variant='h4'
                fontWeight={600}
                color={theme.palette.common.white}
              >
                <Trans i18nKey='sidepanel_container_supporting_you_through_every_stage' />
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Hidden>
      <Grid item className={classes.main}>
        <Box
          display='flex'
          justifyContent='flex-end'
          sx={{paddingRight: '24px', paddingTop: '16px'}}
        >
          <LanguageSelector onWhite />
        </Box>
        <Box
          display={'flex'}
          height={'calc(100% - 56px)'}
          justifyContent='center'
          alignItems={'center'}
          ml={styleProps?.marginLeft || 0}
        >
          {mainComponent}
        </Box>
      </Grid>
    </Grid>
  )
}

export {SidepanelContainer}
